import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';

export default function HistoryPage() {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) fetchHistory();
  }, [user]);

  const fetchHistory = async () => {
    try {
      const token = await user.getIdToken();
      const response = await fetch('https://api.theapplyedge.com/api/v1/history', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch history.');
      const data = await response.json();
      setHistory(data.history || []);
    } catch (e) {
      setError('Failed to load history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    navigate('/');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return `${diff} days ago`;
    return date.toLocaleDateString();
  };

  const handleViewResult = (entry) => {
    navigate('/results', {
      state: {
        results: {
          tailored_cv: entry.tailored_cv,
          cover_letter: entry.cover_letter,
          talking_points: entry.talking_points,
          insights: entry.insights,
          ats_before: entry.ats_before,
          ats_after: entry.ats_after,
          matched_keywords: [],
          missing_keywords: [],
        }
      }
    });
  };

  const handleReoptimise = (entry) => {
    navigate('/app', {
      state: {
        prefillJdText: entry.jd_text,
        prefillMarket: entry.target_market,
      }
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0D0D1A' }}>

      {/* Navbar */}
      <nav style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '16px 40px', borderBottom: '1px solid #2A2A4A'
      }}>
        <div onClick={() => navigate('/app')} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <img src="/app_icon.png" alt="ApplyEdge" style={{ width: 34, height: 34, borderRadius: 9 }} />
          <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, fontSize: 18 }}>ApplyEdge</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ color: '#8888AA', fontSize: 13 }}>{user?.displayName}</div>
          <button onClick={handleSignOut} style={{
            background: 'transparent', border: '1px solid #2A2A4A',
            borderRadius: 8, padding: '6px 14px', color: '#8888AA',
            fontSize: 13, cursor: 'pointer'
          }}>Sign Out</button>
        </div>
      </nav>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 20px' }}>
        <h1 style={{
          fontFamily: 'Space Grotesk, sans-serif', fontSize: 32,
          fontWeight: 700, marginBottom: 8
        }}>History</h1>
        <p style={{ color: '#8888AA', marginBottom: 40 }}>Your last 5 CV optimisations.</p>

        {loading && (
          <div style={{ textAlign: 'center', color: '#0F7DFF', padding: 40 }}>Loading...</div>
        )}

        {error && (
          <div style={{
            background: 'rgba(255,107,107,0.1)', border: '1px solid #FF6B6B',
            borderRadius: 10, padding: 14, color: '#FF6B6B', fontSize: 14
          }}>{error}</div>
        )}

        {!loading && !error && history.length === 0 && (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
            <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 18, fontWeight: 600, color: '#8888AA', marginBottom: 8 }}>
              No history yet
            </div>
            <div style={{ color: '#555570', fontSize: 14 }}>Your optimisations will appear here</div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {history.map((entry) => (
            <div key={entry.id} style={{
              background: '#1A1A2E', border: '1px solid #2A2A4A',
              borderRadius: 16, padding: 24
            }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{
                  background: 'rgba(15,125,255,0.1)', borderRadius: 20,
                  padding: '4px 12px', fontSize: 12, fontWeight: 600, color: '#0F7DFF'
                }}>
                  {entry.target_market}
                </div>
                <div style={{ color: '#555570', fontSize: 12 }}>{formatDate(entry.created_at)}</div>
              </div>

              {/* ATS Scores */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div>
                  <div style={{ color: '#555570', fontSize: 11 }}>Before</div>
                  <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 20, fontWeight: 700, color: '#8888AA' }}>
                    {entry.ats_before}%
                  </div>
                </div>
                <div style={{ color: '#00D4AA', fontSize: 16 }}>→</div>
                <div>
                  <div style={{ color: '#555570', fontSize: 11 }}>After</div>
                  <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 20, fontWeight: 700, color: '#00D4AA' }}>
                    {entry.ats_after}%
                  </div>
                </div>
                <div style={{
                  background: 'rgba(0,212,170,0.1)', borderRadius: 10,
                  padding: '3px 10px', fontSize: 13, fontWeight: 700, color: '#00D4AA'
                }}>
                  +{entry.ats_after - entry.ats_before}%
                </div>
              </div>

              {/* JD Preview */}
              <div style={{ color: '#8888AA', fontSize: 13, lineHeight: 1.5, marginBottom: 16 }}>
                {entry.jd_text?.length > 150 ? `${entry.jd_text.substring(0, 150)}...` : entry.jd_text}
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => handleViewResult(entry)} style={{
                  flex: 1, padding: '10px', background: '#0D0D1A',
                  border: '1px solid #2A2A4A', borderRadius: 10, color: '#fff',
                  fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, cursor: 'pointer'
                }}>View Result</button>
                <button onClick={() => handleReoptimise(entry)} style={{
                  flex: 1, padding: '10px',
                  background: 'linear-gradient(135deg, #0F7DFF, #00D4AA)',
                  border: 'none', borderRadius: 10, color: '#fff',
                  fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, cursor: 'pointer'
                }}>Re-optimise</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}